/* eslint-disable @typescript-eslint/no-unused-vars */
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Checkbox, Icon, IconButton, LinearProgress, Paper, Snackbar, SnackbarCloseReason, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TableRow, Chip, Box, Typography,
  useTheme, alpha
} from '@mui/material';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { LayoutBaseDeFinanceiro } from '../../../shared/layouts';
import { useDebounce } from '../../../shared/hooks';
import { Environment } from '../../../shared/environment';
import dayjs from 'dayjs';
import { FerramentasDaListagemFinanceiro, LinhasEsqueletoTabela } from '../../../shared/components';
import { CelulaListaMobile, LinhaListagemMobile, moedaMobile, useCelular } from '../../../shared/components/listagem-mobile';
import { DialoDesconto } from './DialogDesconto';
import { DialoBaixa } from './DialogBaixa';
import { v4 as uuidv4 } from 'uuid';
import { ContasPagarService, IListagemContasPagar } from '../../../shared/services/api/Financeiro/ContasPagar';
import PDFPagar from './ImpressaoPagar';
import { BasicSelect } from '../../../shared/forms';
import { montarReciboPagamento, montarRecibosDasParcelas } from './ReciboPagamento';
import { IComprovanteBaixaDados } from '../ContasReceber/ComprovanteBaixa';
import { DialogVisualizarComprovante } from '../ContasReceber/DialogVisualizarComprovante';

const isVencida = (vencimento: Date) => {
  return dayjs(vencimento).isBefore(dayjs(), 'day');
};

const formatarCpfCnpj = (cpfCnpj: string | null | undefined) => {
  if (!cpfCnpj) return '';
  const apenasNumeros = cpfCnpj.replace(/\D/g, '');
  if (apenasNumeros.length === 11) {
    return apenasNumeros.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  } else if (apenasNumeros.length === 14) {
    return apenasNumeros.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  }

  return cpfCnpj;
};

// Linha isolada + React.memo: marcar uma conta só re-renderiza as linhas
// cujo objeto mudou (a clicada e a desmarcada), não a tabela inteira.
interface ILinhaContaPagarProps {
  // Celular: a linha vira um cartão (LinhaListagemMobile) no lugar das colunas.
  celular?: boolean;
  row: IListagemContasPagar;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onEditar: (idpagar: string) => void;
  // Conta já paga (total ou parcial): comprovante de pagamento.
  onImprimirComprovante: (row: IListagemContasPagar) => void;
  carregandoComprovante: boolean;
}

const LinhaContaPagar = memo(({ row, onChange, onEditar, onImprimirComprovante, carregandoComprovante, celular }: ILinhaContaPagarProps) => {
  const theme = useTheme();
  const vencida = isVencida(row.vencimento);
  const podeMarcar = row.boleto && !row.status;
  const temPagamento = !!row.status || Number(row.vpago) > 0;
  const botaoComprovante = temPagamento ? (
    <IconButton
      size='small'
      title='Recibo de pagamento (para o parceiro assinar)'
      disabled={carregandoComprovante}
      onClick={(e) => { e.stopPropagation(); onImprimirComprovante(row); }}
      sx={{
        backgroundColor: alpha(theme.palette.success.main, 0.08),
        padding: '3px',
        mr: celular ? 0 : 0.5,
        '&:hover': { backgroundColor: alpha(theme.palette.success.main, 0.15) },
      }}
    >
      <Icon sx={{ fontSize: '16px', color: 'success.main' }}>print</Icon>
    </IconButton>
  ) : null;

  if (celular) {
    const corLinha = !row.boleto ? theme.palette.warning.main : vencida ? theme.palette.error.main : theme.palette.primary.main;
    return (
      <CelulaListaMobile>
        <LinhaListagemMobile
          avatar={row.nome || '?'}
          cor={corLinha}
          titulo={row.nome}
          detalhes={[`Vence ${dayjs(row.vencimento).format('DD/MM/YY')}`, row.parcela ? `Parc. ${row.parcela}` : null, row.numero ? `Nº ${row.numero}` : null]}
          valor={moedaMobile(row.vpagar)}
          corValor={vencida ? 'error.main' : undefined}
          etiqueta={vencida ? { rotulo: 'Vencida', cor: 'error' } : !row.boleto ? { rotulo: 'Não boleto', cor: 'warning' } : undefined}
          destaque={!!row.marcado}
          onClick={() => onEditar(row.idpagar)}
          acoes={podeMarcar ? (
            <Checkbox checked={!!row.marcado} id={row.idpagar} onChange={onChange} color="primary" size="small" />
          ) : botaoComprovante || undefined}
        />
      </CelulaListaMobile>
    );
  }

  return (
    <TableRow
      hover
      sx={{
        cursor: 'pointer',
        backgroundColor: !podeMarcar ? alpha(theme.palette.warning.light, 0.05) :
          row.marcado ? alpha(theme.palette.primary.light, 0.08) : 'inherit',
        '&:hover': {
          backgroundColor: alpha(theme.palette.primary.light, 0.12)
        }
      }}
    >
      <TableCell padding="checkbox" sx={{ width: '5%', px: 0.5 }}>
        {podeMarcar ? (
          <Checkbox
            checked={!!row.marcado}
            id={row.idpagar}
            onChange={onChange}
            color="primary"
            size="small"
          />
        ) : null}
      </TableCell>
      <TableCell
        align="left"
        sx={{
          fontSize: '0.75rem',
          px: 0.5,
          color: !row.boleto ? theme.palette.warning.dark :
            vencida ? theme.palette.error.main : 'inherit'
        }}
      >
        {row.nome}
      </TableCell>
      <TableCell
        align="left"
        sx={{
          fontSize: '0.75rem',
          px: 0.5,
          fontFamily: 'monospace',
          color: !row.boleto ? theme.palette.warning.dark :
            vencida ? theme.palette.error.main : 'inherit'
        }}
      >
        {formatarCpfCnpj(row.cpf)}
      </TableCell>
      <TableCell
        align="center"
        sx={{
          fontSize: '0.75rem',
          px: 0.5,
          color: !row.boleto ? theme.palette.warning.dark :
            vencida ? theme.palette.error.main : 'inherit'
        }}
      >
        {dayjs(row.emissao).format('DD/MM/YY')}
      </TableCell>
      <TableCell
        align="center"
        sx={{
          fontSize: '0.75rem',
          px: 0.5,
          color: !row.boleto ? theme.palette.warning.dark :
            vencida ? theme.palette.error.main : 'inherit'
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="center" flexDirection="column">
          {dayjs(row.vencimento).format('DD/MM/YY')}
          {vencida && (
            <Chip
              label="Vencida"
              size="small"
              color="error"
              sx={{ mt: 0.3, height: '16px', fontSize: '0.55rem', minWidth: '45px' }}
            />
          )}
          {!row.boleto && (
            <Chip
              label="Não Boleto"
              size="small"
              color="warning"
              sx={{ mt: 0.3, height: '16px', fontSize: '0.55rem', minWidth: '55px' }}
            />
          )}
        </Box>
      </TableCell>
      <TableCell
        align="center"
        sx={{
          fontSize: '0.75rem',
          px: 0.5,
          color: !row.boleto ? theme.palette.warning.dark :
            vencida ? theme.palette.error.main : 'inherit'
        }}
      >
        {row.parcela}
      </TableCell>
      <TableCell
        align="right"
        sx={{
          fontSize: '0.75rem',
          px: 0.5,
          fontFamily: 'monospace',
          color: !row.boleto ? theme.palette.warning.dark :
            vencida ? theme.palette.error.main : 'inherit'
        }}
      >
        {row.numero}
      </TableCell>
      <TableCell
        align="right"
        sx={{
          fontSize: '0.75rem',
          px: 0.5,
          fontFamily: 'monospace',
          fontWeight: 'bold',
          color: !row.boleto ? theme.palette.warning.dark :
            vencida ? theme.palette.error.main : 'inherit'
        }}
      >
        {Number(row.vcompra).toLocaleString('pt-BR', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        })}
      </TableCell>
      <TableCell
        align="right"
        sx={{
          fontSize: '0.75rem',
          px: 0.5,
          fontFamily: 'monospace',
          color: !row.boleto ? theme.palette.warning.dark :
            vencida ? theme.palette.error.main : 'inherit'
        }}
      >
        {Number(row.vparcela).toLocaleString('pt-BR', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        })}
      </TableCell>
      <TableCell
        align="right"
        sx={{
          fontSize: '0.75rem',
          px: 0.5,
          fontFamily: 'monospace',
          color: theme.palette.warning.dark,
        }}
      >
        {Number(row.acrescimo).toLocaleString('pt-BR', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        })}
      </TableCell>
      <TableCell
        align="right"
        sx={{
          fontSize: '0.75rem',
          px: 0.5,
          fontFamily: 'monospace',
          color: theme.palette.success.main,
        }}
      >
        {Number(row.vpago).toLocaleString('pt-BR', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        })}
      </TableCell>
      <TableCell
        align="right"
        sx={{
          fontSize: '0.75rem',
          px: 0.5,
          fontFamily: 'monospace',
          color: theme.palette.info.main,
        }}
      >
        {Number(row.desconto).toLocaleString('pt-BR', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        })}
      </TableCell>
      <TableCell
        align="right"
        sx={{
          fontSize: '0.75rem',
          px: 0.5,
          fontFamily: 'monospace',
          fontWeight: 'bold',
          color: vencida ? theme.palette.error.main :
            !row.boleto ? theme.palette.warning.dark : 'inherit'
        }}
      >
        {Number(row.vpagar).toLocaleString('pt-BR', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        })}
      </TableCell>
      <TableCell align="center" sx={{ px: 0.5, whiteSpace: 'nowrap' }}>
        {botaoComprovante}
        <IconButton
          size='small'
          onClick={() => onEditar(row.idpagar)}
          sx={{
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
            padding: '3px',
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.15)
            }
          }}
        >
          <Icon sx={{ fontSize: '16px' }}>edit</Icon>
        </IconButton>
      </TableCell>
    </TableRow>
  );
});
LinhaContaPagar.displayName = 'LinhaContaPagar';

export const ListagemContasPagar: React.FC = () => {
  const listaCelular = useCelular();
  const theme = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const { debounce } = useDebounce();
  const navigate = useNavigate();
  const location = useLocation();
  const [rows, setRows] = useState<IListagemContasPagar[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [istexto, setIsTexto] = useState(false);
  const [isAutoComplete, setIsAutoComplete] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [total, setTotal] = useState(0);
  const [selecionado, setSelecionado] = useState(0);
  const [open, setOpen] = useState(false);
  const [openBaixa, setOpenBaixa] = useState(false);
  const [nome, setNome] = useState('');
  const [data, setData] = useState(true);
  const [bstatus, setBstatus] = useState(true);
  const [openmsg, setOpenmsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [tms, setTms] = useState<'warning' | 'error' | 'info' | 'success'>('warning');

  const primeiroDiaMes = dayjs().startOf('month');
  const ultimoDiaMes = dayjs().endOf('month');

  const [dataInicial, setDataInicial] = useState(primeiroDiaMes.format('YYYY-MM-DD'));
  const [dataFinal, setDataFinal] = useState(ultimoDiaMes.format('YYYY-MM-DD'));

  const handleCloseMsg = (
    event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenmsg(false);
  };

  // Estável entre renders (antes era um uuidv4() novo a cada render, o que
  // disparava o efeito de `value` do DialoBaixa em todo clique) — gera um
  // novo só ao abrir a baixa, em handleBaixa.
  const [guuid, setGuuid] = useState(() => uuidv4());

  const handleEditar = useCallback((idpagar: string) => {
    navigate(`/contaspagar/${idpagar}`, { state: { from: `${location.pathname}${location.search}` } });
  }, [navigate, location.pathname, location.search]);

  // Comprovante de uma conta já paga: favorecido, como foi pago (forma e
  // conta de saída) e um campo pra escrever o que foi combinado — mesmo
  // visualizador do Contas a Receber (ver DialogVisualizarComprovante).
  const [comprovanteDados, setComprovanteDados] = useState<IComprovanteBaixaDados | null>(null);
  const [openComprovante, setOpenComprovante] = useState(false);
  const [carregandoComprovante, setCarregandoComprovante] = useState<string | null>(null);
  // Parcelas do lançamento da conta do recibo aberto (opção "Todas as parcelas").
  const [reciboParcelas, setReciboParcelas] = useState<{ row: IListagemContasPagar; parcelas: { idpagar: string; numero: string; parcela: string; vparcela: number }[]; historico?: string } | null>(null);
  const handleImprimirComprovante = useCallback(async (row: IListagemContasPagar) => {
    setCarregandoComprovante(row.idpagar);
    // Parcelas do mesmo lançamento (getById já traz) — pro "Todas as parcelas".
    ContasPagarService.getById(row.idpagar).then((conta) => {
      setReciboParcelas(conta instanceof Error ? null : { row, parcelas: conta.parcelas || [], historico: conta.historico });
    });
    const recibo = await montarReciboPagamento({
      idpagar: row.idpagar,
      parceiro: row.nome,
      documentoParceiro: row.cpf,
      numero: row.numero,
      parcela: row.parcela,
      valorEmAberto: Number(row.vpagar),
    });
    setCarregandoComprovante(null);
    if (recibo instanceof Error) {
      setTms('error');
      setMsg(recibo.message);
      setOpenmsg(true);
      return;
    }
    setComprovanteDados(recibo);
    setOpenComprovante(true);
  }, []);

  const busca = useMemo(() => {
    return searchParams.get('busca') || '';
  }, [searchParams]);

  const basicstatus = useMemo(() => {
    return searchParams.get('basicstatus') || 'false';
  }, [searchParams]);

  const parceiroselect = useMemo(() => {
    return searchParams.get('parceiroselect') || '';
  }, [searchParams]);

  const tipo = useMemo(() => {
    return searchParams.get('tipo') || 'data';
  }, [searchParams]);

  const idparceiro = useMemo(() => {
    return searchParams.get('parceiro') || '';
  }, [searchParams]);

  useEffect(() => {
    const urlDataI = searchParams.get('datainicial') || dataInicial;
    const urlDataF = searchParams.get('datafinal') || dataFinal;
    setDataInicial(urlDataI);
    setDataFinal(urlDataF);
  }, [searchParams]);

  useEffect(() => {
    if (tipo === 'data') {
      setData(true);
      setIsTexto(false);
      setIsAutoComplete(false);
      setRows([]);
      const primeiroDiaMes = dayjs().startOf('month');
      const ultimoDiaMes = dayjs().endOf('month');

      setDataInicial(primeiroDiaMes.format('YYYY-MM-DD'));
      setDataFinal(ultimoDiaMes.format('YYYY-MM-DD'));

      setSearchParams({
        tipo: 'data',
        datainicial: primeiroDiaMes.format('YYYY-MM-DD'),
        datafinal: ultimoDiaMes.format('YYYY-MM-DD')
      }, { replace: true });

      setTotalCount(0);
      setBstatus(true);
    }
    else if (tipo === 'parceiro') {
      setIsAutoComplete(true);
      setIsTexto(false);
      setData(false);
      setRows([]);
      // Preserva parceiro/parceiroselect já presentes na URL — sem isso,
      // remontar a listagem com tipo=parceiro já na URL (ex.: voltando da
      // tela de "Novo" pelo botão Voltar) apagava o parceiro pesquisado
      // nesse mesmo instante, porque esse efeito roda de novo em toda
      // montagem com tipo==='parceiro', não só quando o usuário troca PRA
      // essa aba manualmente.
      setSearchParams({ tipo: 'parceiro', parceiro: idparceiro, parceiroselect: parceiroselect }, { replace: true });
      setTotalCount(0);
      setBstatus(false);
    }
    else if (tipo === 'pagas') {
      // Contas já pagas, filtradas pela data em que o pagamento aconteceu —
      // parceiro é opcional aqui (ver efeito de busca abaixo), então mostra
      // os dois filtros (data + autocomplete) juntos.
      setIsAutoComplete(true);
      setIsTexto(false);
      setData(true);
      setRows([]);
      const primeiroDiaMes = dayjs().startOf('month');
      const ultimoDiaMes = dayjs().endOf('month');

      setDataInicial(primeiroDiaMes.format('YYYY-MM-DD'));
      setDataFinal(ultimoDiaMes.format('YYYY-MM-DD'));

      // Datas sempre resetam pro mês corrente (pedido explícito), mas
      // parceiro/parceiroselect são preservados — mesmo raciocínio do
      // branch 'parceiro' acima: sem isso, voltar da tela de "Novo" com
      // esse tipo já na URL apagava o parceiro pesquisado.
      setSearchParams({
        tipo: 'pagas',
        datainicial: primeiroDiaMes.format('YYYY-MM-DD'),
        datafinal: ultimoDiaMes.format('YYYY-MM-DD'),
        parceiro: idparceiro,
        parceiroselect: parceiroselect,
      }, { replace: true });

      setTotalCount(0);
      // Só visualização — já foi baixada, não tem o que Baixar/Descontar/
      // Salvar de novo nela.
      setBstatus(true);
    } else {
      setIsTexto(true);
      setIsAutoComplete(false);
      setData(false);
      setRows([]);
      setSearchParams({ tipo: 'r.numerodoc' }, { replace: true });
      setTotalCount(0);
      setBstatus(true);
    }
  }, [tipo]);

  useEffect(() => {
    if (tipo === 'r.numerodoc' && busca != '') {
      setIsLoading(true);
      debounce(() => {
        ContasPagarService.getAll(1, '', '', busca, 'numero')
          .then((result) => {
            setIsLoading(false);
            if (result instanceof Error) {
              setTms('error');
              setMsg(result.message);
              setOpenmsg(true);
            } else {
              setTotalCount(result.totalCount);
              setSelecionado(result.selecionado);
              setTotal(result.total);
              setRows(result.data);
            }
          });
      });
    }
    setIsLoading(false);
    // `tipo` entra na dependência pra SEMPRE refazer a busca ao trocar de
    // aba — sem isso, voltar pra "Numero" com o mesmo texto já digitado
    // antes não disparava nada (nem `busca` nem `tipo` sozinhos mudavam de
    // valor o bastante pra React perceber, já que só um dos dois mudava a
    // cada troca de aba).
  }, [busca, tipo]);

  useEffect(() => {
    if (tipo === 'data' && dataInicial && dataFinal) {
      setIsLoading(true);
      debounce(() => {
        ContasPagarService.getAll(1, dataInicial, dataFinal, '', 'data')
          .then((result) => {
            setIsLoading(false);
            if (result instanceof Error) {
              setTms('error');
              setMsg(result.message);
              setOpenmsg(true);
            } else {
              setTotalCount(result.totalCount);
              // Vencimento é seleção única — começa sempre sem nada marcado.
              setRows(result.data.map(row => ({ ...row, marcado: false })));
              setSelecionado(0);
              setTotal(result.total);
            }
          });
      });
    } else if (tipo !== 'pagas') {
      // Sem esse guard, trocar dataInicial/dataFinal na aba "Pagas" também
      // disparava este efeito (mesma dependência) e apagava com setRows([])
      // o que o efeito de 'pagas' logo abaixo ia preencher — inofensivo no
      // resultado final (a busca assíncrona chega depois e sobrescreve),
      // mas causava um flash vazio desnecessário.
      setRows([]);
    }
    // Mesmo raciocínio acima: sem `tipo` aqui, reabrir a aba "Vencimento"
    // com o mesmo mês corrente de antes (dataInicial/dataFinal batendo com
    // o valor já salvo no state) não reexecutava a busca, porque
    // setDataInicial/setDataFinal com o MESMO texto não conta como mudança
    // pro React.
  }, [dataInicial, dataFinal, tipo]);

  useEffect(() => {
    if (tipo === 'parceiro' && idparceiro != '') {
      setIsLoading(true);
      setNome(parceiroselect);
      debounce(() => {
        ContasPagarService.getAll(1, '', '', idparceiro, 'parceiro')
          .then((result) => {
            setIsLoading(false);
            if (result instanceof Error) {
              setTms('error');
              setMsg(result.message);
              setOpenmsg(true);
            } else {
              setTotalCount(result.totalCount);
              setRows(result.data);
              setSelecionado(0);
              setTotal(result.total);
            }
          });
      });
    } else if (tipo !== 'pagas') {
      setRows([]);
    }
  }, [idparceiro, tipo]);

  // Contas já pagas, filtradas pela data do pagamento — `idparceiro` é
  // OPCIONAL aqui (ver ContasPagarService.getAll/backend GetAll.ts, tipo
  // 'pagas'): vazio traz as pagas de todos os parceiros no período, com
  // parceiro selecionado filtra só as dele.
  useEffect(() => {
    if (tipo === 'pagas' && dataInicial && dataFinal) {
      setIsLoading(true);
      if (idparceiro) setNome(parceiroselect);
      debounce(() => {
        ContasPagarService.getAll(1, dataInicial, dataFinal, idparceiro, 'pagas')
          .then((result) => {
            setIsLoading(false);
            if (result instanceof Error) {
              setTms('error');
              setMsg(result.message);
              setOpenmsg(true);
            } else {
              setTotalCount(result.totalCount);
              setRows(result.data);
              setSelecionado(0);
              setTotal(result.total);
            }
          });
      });
    } else if (tipo !== 'parceiro') {
      setRows([]);
    }
  }, [dataInicial, dataFinal, idparceiro, tipo]);

  // Usa a forma funcional de setRows (prevRows => ...) em vez de fechar
  // sobre `rows` direto — cliques rápidos em sequência (um checkbox logo
  // depois do outro, antes do React re-renderizar) cada um pegava o MESMO
  // `rows` desatualizado do momento em que handleChange foi criado, então o
  // segundo clique podia "não ver" a marcação que o primeiro acabara de
  // fazer, dando a impressão de que a anterior não tinha sido desmarcada.
  // Também evita recriar objeto pras linhas que não mudaram (só a que foi
  // clicada e, no modo Vencimento, a que era a marcada antes) — com uma
  // listagem grande, marcar sempre recriava a lista inteira mesmo quando só
  // 1-2 linhas de fato mudavam, o que pesava a cada seleção.
  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const { id, checked } = event.target;

    // Baixa por Vencimento só é permitida um registro por vez — marcar um
    // aqui desmarca qualquer outro que já estivesse marcado, em vez de
    // acumular seleção como nas outras abas.
    // Desmarca TODAS as outras (não só a primeira marcada encontrada) — a
    // listagem pode chegar com mais de uma já marcada.
    if (tipo === 'data') {
      setRows(prevRows => {
        const index = prevRows.findIndex(row => row.idpagar === id);
        if (index === -1) return prevRows;

        setSelecionado(checked ? Number(prevRows[index].vpagar) : 0);
        return prevRows.map((row, i) => {
          const marcado = checked && i === index;
          return row.marcado === marcado ? row : { ...row, marcado };
        });
      });
      return;
    }

    setRows(prevRows => {
      const index = prevRows.findIndex(row => row.idpagar === id);
      if (index === -1) return prevRows;

      const updatedRow = { ...prevRows[index], marcado: checked };
      const valorNumerico = Number(updatedRow.vpagar);
      const valueToUpdate = checked ? valorNumerico : -valorNumerico;
      setSelecionado(currentSelecionado => currentSelecionado + valueToUpdate);

      return [...prevRows.slice(0, index), updatedRow, ...prevRows.slice(index + 1)];
    });
  }, [tipo]);

  const handleSelectAllChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target;
    const updatedRows = rows.map(row => {
      if (row.boleto) {
        return { ...row, marcado: checked };
      } else {
        return { ...row, marcado: false };
      }
    });
    setRows(updatedRows);

    if (checked) {
      const totalSelecionado = updatedRows
        .filter(row => row.boleto && row.marcado)
        .reduce((acc, row) => {
          const valorNumerico = Number(row.vpagar);
          return acc + valorNumerico;
        }, 0);
      setSelecionado(totalSelecionado);
    } else {
      setSelecionado(0);
    }
  };

  const handleClose = (newValue?: string) => {
    setOpen(false);
    if (newValue) {
      // setValue(newValue); 
    }
  };

  // Depois da baixa (ou ao cancelar), recarrega a MESMA aba com os mesmos
  // filtros da URL — handleBaixa deixou só as contas marcadas na lista, então
  // sempre precisa buscar de novo. Pesquisando por parceiro, continua no
  // mesmo parceiro em vez de voltar pra listagem por Vencimento.
  const handleCloseBaixa = (newValue?: string) => {
    setOpenBaixa(false);
    const recarregar =
      tipo === 'data' ? ContasPagarService.getAll(1, dataInicial, dataFinal, '', 'data')
        : tipo === 'parceiro' && idparceiro ? ContasPagarService.getAll(1, '', '', idparceiro, 'parceiro')
          : tipo === 'r.numerodoc' && busca ? ContasPagarService.getAll(1, '', '', busca, 'numero')
            : null;
    if (!recarregar) return;
    setIsLoading(true);
    recarregar.then((result) => {
      setIsLoading(false);
      if (result instanceof Error) {
        setTms('error');
        setMsg(result.message);
        setOpenmsg(true);
        return;
      }
      // Mesmo tratamento da carga inicial de cada aba (efeitos de busca
      // acima): Vencimento começa sem nada marcado; Parceiro/Número mantêm
      // as marcações salvas que vêm do backend.
      setTotalCount(result.totalCount);
      setTotal(result.total);
      if (tipo === 'data') {
        setSelecionado(0);
        setRows(result.data.map(row => ({ ...row, marcado: false })));
      } else {
        setSelecionado(tipo === 'r.numerodoc' ? result.selecionado : 0);
        setRows(result.data);
      }
    });
  };

  const handleDesconto = () => {
    if (totalCount != 0) {
      const filteredList = rows.filter((item) => item.marcado === true);
      setRows(filteredList);
      setOpen(true);
    }
  };

  const handleBaixa = () => {
    if (totalCount != 0) {
      const filteredList = rows.filter((item) => item.marcado === true);
      // Na aba Vencimento a baixa é uma conta por vez (ver handleChange).
      if (tipo === 'data' && filteredList.length !== 1) {
        setTms('warning');
        setMsg('Selecione uma conta para dar baixa');
        setOpenmsg(true);
        return;
      }
      setRows(filteredList);
      setGuuid(uuidv4());
      setOpenBaixa(true);
    }
  };

  const handleSave = async () => {
    if (totalCount != 0) {
      setIsLoading(true);
      const marcados = rows.map(listItem => ({
        idpagar: listItem.idpagar,
        marcado: listItem.marcado,
        desconto: listItem.desconto,
        vpagar: listItem.vpagar,
      }));
      try {
        const result = await ContasPagarService.Marcados(marcados);
        setIsLoading(false);
        if (result instanceof Error) {
          setTms('error');
          setMsg(result.message);
          setOpenmsg(true);
        }
      } catch (error) {
        setIsLoading(false);
        setTms('error');
        setMsg('Ocorreu um erro ao processar.');
        setOpenmsg(true);
      }
    }
  };

  const handleDesc = (dados?: IListagemContasPagar[]) => {
    if (dados) {
      setRows(dados);
    }
  };

  const handletotal = (valor?: number) => {
    if (valor) {
      setSelecionado(valor);
    }
  };
  const handlePrint = () => {
    setIsLoading(true);
    if (selecionado != 0 && totalCount != 0) {
      const filteredList = rows.filter((item) => item.marcado === true);
      PDFPagar(filteredList, total, nome);
    } else {
      setTms('error');
      setMsg('Selecione uma Conta');
      setOpenmsg(true);
      setIsLoading(false);
    }
    setIsLoading(false);
  };
  return (
    <LayoutBaseDeFinanceiro
      titulo='Contas a Pagar'
      labelSelecionado='Selecionado'
      labeltotal='Total a Pagar'
      total={total.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      selecionado={selecionado.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      barraDeFerramentas={
        <FerramentasDaListagemFinanceiro
          mostrarInputBusca={istexto}
          mostrarInputData={tipo === 'data' || tipo === 'pagas'}
          mostrarAutoComplete={isAutoComplete}
          mostrarInputBasicSelect
          mostrarOpcaoPagas
          textSelectFalse='Pagar'
          textSelectTrue='Pagas'
          textoDaBusca={busca}
          dataInicial={dataInicial}
          dataFinal={dataFinal}
          textoDoSelect={tipo}
          textoDoStatus={basicstatus}
          // Vencimento libera só a Baixa (uma conta por vez); Desconto e
          // Salvar continuam só na aba Parceiro.
          disbilitarBaixar={bstatus && tipo !== 'data'}
          disbilitarDesconto={bstatus}
          disbilitarSalvar={bstatus}
          disbilitarImprimir={
            tipo !== 'parceiro' || basicstatus === 'true'
          }
          textoBotaoNovo='Adicionar'
          // Leva o parceiro filtrado na listagem (aba Parceiro ou Pagas por
          // período) já pré-selecionado pro novo lançamento — ver
          // DetalhesDePagar.tsx.
          aoClicarEmNovo={() => navigate('/contaspagar/novo', {
            state: {
              from: `${location.pathname}${location.search}`,
              ...(idparceiro ? { idparceiro } : {}),
            }
          })}
          // Preserva o parceiro filtrado visualmente no autocomplete (ver
          // AutoCompleteParceiroListagem.tsx) — sem isso o campo voltava em
          // branco mesmo com a listagem já filtrada por esse parceiro.
          valorInicialParceiro={idparceiro ? { idparceiro, nome: parceiroselect } : null}
          aoClicarEmDesconto={handleDesconto}
          aoClicarEmSalvar={handleSave}
          aoClicarEmImpressao={() => handlePrint()}
          aoClicarEmBaixa={handleBaixa}
          aoMudarDataInicial={dataInicial =>
            setSearchParams({
              tipo: tipo,
              datainicial: dataInicial,
              datafinal: dataFinal,
              parceiro: idparceiro,
              parceiroselect: parceiroselect,
            }, { replace: true })
          }
          aoMudarDataFinal={datafinal =>
            setSearchParams({
              tipo: tipo,
              datainicial: dataInicial,
              datafinal: datafinal,
              parceiro: idparceiro,
              parceiroselect: parceiroselect,
            }, { replace: true })
          }
          aoMudarTextoDeBusca={texto =>
            setSearchParams({
              busca: texto,
              tipo: tipo,
              datainicial: dataInicial,
              datafinal: dataFinal
            }, { replace: true })
          }
          aoMudarTextoDoSelect={textoselect =>
            setSearchParams({
              busca: busca,
              tipo: textoselect,
              datainicial: dataInicial,
              datafinal: dataFinal
            }, { replace: true })
          }
          aoMudarTextoDoParceiro={(texto, texto1) =>
            setSearchParams({
              parceiroselect: texto1,
              parceiro: texto,
              busca: busca,
              tipo: tipo,
              datainicial: dataInicial,
              datafinal: dataFinal
            }, { replace: true })
          }
        />
      }
    >
      <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={openmsg} autoHideDuration={tms === 'error' ? null : 900} onClose={handleCloseMsg}>
        <Alert
          onClose={handleCloseMsg}
          severity={tms}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {msg}
        </Alert>
      </Snackbar>

      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{
          mx: 1,
          mb: 1,
          borderRadius: 2,
          overflow: 'auto',
          maxHeight: 'calc(100vh - 200px)',
          boxShadow: theme.shadows[1],
          '&:hover': {
            boxShadow: theme.shadows[2]
          },
          width: '98%'
        }}
      >
        <Table
          size="small"
          sx={{
            minWidth: listaCelular ? 0 : 1200,
            tableLayout: 'auto',
          }}
        >
          {!listaCelular && (
          <TableHead>
            <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.08) }}>
              <TableCell padding="checkbox" sx={{ width: '5%', px: 0.5 }}>
                {tipo !== 'data' && tipo !== 'pagas' && (
                  <Checkbox
                    checked={rows.length > 0 && rows.every(row => row.marcado)}
                    onChange={handleSelectAllChange}
                    color="primary"
                    size="small"
                  />
                )}
              </TableCell>
              <TableCell align="left" sx={{ fontWeight: 'bold', width: '15%', px: 0.5 }}>Parceiro</TableCell>
              <TableCell align="left" sx={{ fontWeight: 'bold', width: '12%', px: 0.5 }}>CPF/CNPJ</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold', width: '8%', px: 0.5 }}>Emissão</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold', width: '10%', px: 0.5 }}>Vencimento</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold', width: '6%', px: 0.5 }}>Parcela</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', width: '10%', px: 0.5 }}>Número</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', width: '8%', px: 0.5 }}>Valor</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', width: '8%', px: 0.5 }}>V.Parcela</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', width: '8%', px: 0.5 }}>Juros/Acré.</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', width: '8%', px: 0.5 }}>Pago</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', width: '8%', px: 0.5 }}>Desc</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', width: '9%', px: 0.5 }}>V.Pagar</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold', width: '7%', px: 0.5 }}>Ações</TableCell>
            </TableRow>
          </TableHead>
          )}
          {isLoading && rows.length === 0 && (
            <TableBody>
              <LinhasEsqueletoTabela
                colunas={[
                  { variant: 'rounded', width: 18, height: 18, align: 'center' },
                  { width: '80%' },
                  { width: '70%' },
                  { width: '55%', align: 'center' },
                  { width: '55%', align: 'center' },
                  { width: '40%', align: 'center' },
                  { width: '50%', align: 'right' },
                  { width: '55%', align: 'right' },
                  { width: '55%', align: 'right' },
                  { width: '55%', align: 'right' },
                  { width: '55%', align: 'right' },
                  { width: '55%', align: 'right' },
                  { width: '60%', align: 'right' },
                  { variant: 'circular', width: 24, height: 24, align: 'center' },
                ]}
              />
            </TableBody>
          )}
          <TableBody>
            {rows.map(row => (
              <LinhaContaPagar
                key={row.idpagar}
                row={row}
                celular={listaCelular}
                onChange={handleChange}
                onEditar={handleEditar}
                onImprimirComprovante={handleImprimirComprovante}
                carregandoComprovante={carregandoComprovante === row.idpagar}
              />
            ))}
          </TableBody>

          {totalCount === 0 && !isLoading && (
            <TableRow>
              <TableCell colSpan={14} align="center" sx={{ py: 3 }}>
                <Box display="flex" flexDirection="column" alignItems="center">
                  <Icon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }}>receipt</Icon>
                  <Typography variant="h6" color="text.secondary">
                    {Environment.LISTAGEM_VAZIA}
                  </Typography>
                </Box>
              </TableCell>
            </TableRow>
          )}

          <TableFooter>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={14}>
                  <LinearProgress variant='indeterminate' />
                </TableCell>
              </TableRow>
            )}
          </TableFooter>
        </Table>
      </TableContainer>

      <DialoDesconto
        open={open}
        onClose={handleClose}
        dados={rows}
        onDados={handleDesc}
        ontotal={handletotal}
        vselecionado={selecionado}
      />

      <DialoBaixa
        value={guuid}
        open={openBaixa}
        onClose={handleCloseBaixa}
        dados={rows}
        onDados={handleDesc}
        ontotal={handletotal}
        vselecionado={selecionado}
      />

      <DialogVisualizarComprovante
        open={openComprovante}
        dados={comprovanteDados}
        onClose={() => setOpenComprovante(false)}
        qtdParcelas={reciboParcelas?.parcelas.length || 1}
        carregarTodas={reciboParcelas ? () => montarRecibosDasParcelas(reciboParcelas.parcelas, {
          parceiro: reciboParcelas.row.nome,
          documentoParceiro: reciboParcelas.row.cpf,
          historico: reciboParcelas.historico,
        }) : undefined}
      />
    </LayoutBaseDeFinanceiro>
  );
};